import React, { useEffect, useState } from 'react';
import { Globe, ArrowRightLeft, Send, History, Users, CheckCircle2 } from 'lucide-react';
import { useAppContext, Transfer } from '../contexts/AppContext';
import { formatCurrency, formatForeignCurrency, formatDate, generateReference } from '../utils/formatters';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { EmptyState } from '../components/ui/EmptyState';
import { toast } from 'sonner';
const EXCHANGE_RATES: Record<string, number> = {
  USD: 1550,
  GBP: 1950,
  EUR: 1680
};
const BANKS = ['GTBank', 'Access Bank', 'FirstBank', 'UBA', 'Zenith Bank', 'Kuda Bank', 'Opay'];
export const DiasporaExchange: React.FC = () => {
  const {
    state,
    dispatch,
    processTransaction
  } = useAppContext();
  const [activeTab, setActiveTab] = useState<'send' | 'beneficiaries' | 'history'>('send');
  // Form State
  const [currency, setCurrency] = useState('USD');
  const [amount, setAmount] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [bankName, setBankName] = useState(BANKS[0]);
  const [accountNumber, setAccountNumber] = useState('');
  const [purpose, setPurpose] = useState('Family Support');
  // UI State
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentRef, setCurrentRef] = useState('');
  // Calculations
  const numAmount = Number(amount) || 0;
  const rate = EXCHANGE_RATES[currency];
  const fee = numAmount * 0.015; // 1.5% fee
  const totalCharge = numAmount + fee;
  const ngnAmount = numAmount * rate;
  // Mock Beneficiaries (derived from past transfers)
  const beneficiaries = Array.from(new Set(state.transfers.map(t => JSON.stringify({
    name: t.recipientName,
    bank: t.bankName,
    account: t.accountNumber
  })))).map(s => JSON.parse(s));
  // Simulate account lookup
  useEffect(() => {
    if (accountNumber.length === 10) {
      // Mock lookup
      setRecipientName(accountNumber.startsWith('01') ? 'John Doe' : 'Sarah Smith');
    } else if (accountNumber.length < 10) {
      setRecipientName('');
    }
  }, [accountNumber]);
  const handleSendSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (numAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (accountNumber.length !== 10) {
      toast.error('Account number must be 10 digits');
      return;
    }
    setIsConfirmOpen(true);
  };
  const confirmTransfer = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setIsConfirmOpen(false);
      const reference = generateReference('TRF');
      setCurrentRef(reference);
      const newTransfer: Transfer = {
        id: `trf_${Date.now()}`,
        sourceCurrency: currency,
        sourceAmount: numAmount,
        ngnAmount: ngnAmount,
        recipientName,
        bankName,
        accountNumber,
        fee,
        exchangeRate: rate,
        status: 'Completed',
        date: new Date().toISOString(),
        reference
      };
      dispatch({
        type: 'ADD_TRANSFER',
        payload: newTransfer
      });
      // Note: In a real app, this might deduct from a linked foreign card.
      // For demo, we just record the transaction in the global history.
      dispatch({
        type: 'ADD_TRANSACTION',
        payload: {
          id: `tx_${Date.now()}`,
          type: 'Diaspora',
          title: `Transfer to ${recipientName}`,
          amount: -ngnAmount,
          status: 'Completed',
          date: new Date().toISOString(),
          reference
        }
      });
      setIsSuccessOpen(true);
      // Reset form
      setAmount('');
      setAccountNumber('');
      setRecipientName('');
    }, 1500);
  };
  const fillBeneficiary = (b: any) => {
    setRecipientName(b.name);
    setBankName(b.bank);
    setAccountNumber(b.account);
    setActiveTab('send');
  };
  return <div className="space-y-6" data-id="element-469">
      {/* Header */}
      <div data-id="element-470">
        <h1 className="text-2xl font-bold text-brand-text-dark" data-id="element-471">
          Diaspora Exchange
        </h1>
        <p className="text-brand-text-light text-sm" data-id="element-472">
          Send money home instantly with competitive rates.
        </p>
      </div>

      <div className="flex space-x-1 bg-white p-1 rounded-xl border border-gray-100 inline-flex" data-id="element-473">
        <button onClick={() => setActiveTab('send')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'send' ? 'bg-brand-primary/10 text-brand-primary-dark' : 'text-brand-text-light hover:bg-gray-50'}`} data-id="element-474">
          <Send size={16} data-id="element-475" /> Send Money
        </button>
        <button onClick={() => setActiveTab('beneficiaries')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'beneficiaries' ? 'bg-brand-primary/10 text-brand-primary-dark' : 'text-brand-text-light hover:bg-gray-50'}`} data-id="element-476">
          <Users size={16} data-id="element-477" /> Beneficiaries
        </button>
        <button onClick={() => setActiveTab('history')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'history' ? 'bg-brand-primary/10 text-brand-primary-dark' : 'text-brand-text-light hover:bg-gray-50'}`} data-id="element-478">
          <History size={16} data-id="element-479" /> History
        </button>
      </div>

      {activeTab === 'send' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-480">
          <div className="lg:col-span-2 bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-8" data-id="element-481">
            <form onSubmit={handleSendSubmit} className="space-y-8" data-id="element-482">
              {/* Calculator Section */}
              <div className="relative" data-id="element-483">
                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100" data-id="element-484">
                  <label className="block text-sm font-medium text-gray-500 mb-2" data-id="element-485">
                    You Send
                  </label>
                  <div className="flex items-center gap-4" data-id="element-486">
                    <select value={currency} onChange={e => setCurrency(e.target.value)} className="bg-white border border-gray-200 rounded-xl px-4 py-3 font-semibold text-brand-text-dark focus:outline-none focus:ring-2 focus:ring-brand-primary/20" data-id="element-487">
                      <option value="USD" data-id="element-488">USD</option>
                      <option value="GBP" data-id="element-489">GBP</option>
                      <option value="EUR" data-id="element-490">EUR</option>
                    </select>
                    <input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="bg-transparent text-4xl font-bold text-brand-text-dark w-full focus:outline-none" placeholder="0.00" min="1" step="0.01" data-id="element-491" />
                  </div>
                </div>

                <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 z-10" data-id="element-492">
                  <div className="w-12 h-12 bg-white rounded-full border border-gray-100 shadow-sm flex items-center justify-center text-brand-primary" data-id="element-493">
                    <ArrowRightLeft size={20} className="rotate-90" data-id="element-494" />
                  </div>
                </div>

                <div className="bg-brand-primary/5 rounded-2xl p-5 border border-brand-primary/20 mt-2" data-id="element-495">
                  <label className="block text-sm font-medium text-brand-primary-dark mb-2" data-id="element-496">
                    Recipient Gets
                  </label>
                  <div className="flex items-center gap-4" data-id="element-497">
                    <div className="bg-white border border-brand-primary/20 rounded-xl px-4 py-3 font-semibold text-brand-text-dark flex items-center gap-2" data-id="element-498">
                      <img src="https://flagcdn.com/w20/ng.png" alt="NGN" className="w-5 h-auto rounded-sm" data-id="element-499" />{' '}
                      NGN
                    </div>
                    <input type="text" value={formatCurrency(ngnAmount).replace('₦', '')} readOnly className="bg-transparent text-4xl font-bold text-brand-text-dark w-full focus:outline-none" placeholder="0.00" data-id="element-500" />
                  </div>
                </div>
              </div>

              {/* Recipient Details */}
              <div className="space-y-5" data-id="element-501">
                <h3 className="font-semibold text-brand-text-dark border-b border-gray-100 pb-2" data-id="element-502">
                  Recipient Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-503">
                  <div data-id="element-504">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-505">
                      Bank Name
                    </label>
                    <select value={bankName} onChange={e => setBankName(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-506">
                      {BANKS.map(b => <option key={b} value={b} data-id="element-507">
                          {b}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-508">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-509">
                      Account Number
                    </label>
                    <input type="text" maxLength={10} value={accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" placeholder="10 digit number" data-id="element-510" />
                  </div>
                  <div className="md:col-span-2" data-id="element-511">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-512">
                      Account Name
                    </label>
                    <input type="text" value={recipientName} readOnly className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 outline-none" placeholder="Auto-filled on valid account" data-id="element-513" />
                  </div>
                  <div className="md:col-span-2" data-id="element-514">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-515">
                      Purpose of Transfer
                    </label>
                    <select value={purpose} onChange={e => setPurpose(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-516">
                      <option data-id="element-517">Family Support</option>
                      <option data-id="element-518">Business</option>
                      <option data-id="element-519">Savings</option>
                      <option data-id="element-520">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={!recipientName || numAmount <= 0} className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-4 rounded-xl font-semibold text-lg transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-521">
                Continue to Review <ArrowRightLeft size={20} data-id="element-522" />
              </button>
            </form>
          </div>

          {/* Rate Info Card */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 h-fit sticky top-24" data-id="element-523">
            <h3 className="font-semibold text-brand-text-dark mb-4 flex items-center gap-2" data-id="element-524">
              <Globe className="text-brand-primary" size={20} data-id="element-525" /> Exchange Rate
            </h3>
            <div className="bg-gray-50 rounded-xl p-4 mb-6" data-id="element-526">
              <p className="text-sm text-brand-text-light mb-1" data-id="element-527">Current Rate</p>
              <p className="text-2xl font-bold text-brand-text-dark" data-id="element-528">
                1 {currency} = ₦{rate.toLocaleString()}
              </p>
              <p className="text-xs text-brand-primary font-medium mt-2 flex items-center gap-1" data-id="element-529">
                <CheckCircle2 size={12} data-id="element-530" /> Guaranteed for 30 mins
              </p>
            </div>

            <div className="space-y-3 text-sm" data-id="element-531">
              <div className="flex justify-between" data-id="element-532">
                <span className="text-brand-text-light" data-id="element-533">Transfer Amount</span>
                <span className="font-medium" data-id="element-534">
                  {formatForeignCurrency(numAmount, currency)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-535">
                <span className="text-brand-text-light" data-id="element-536">
                  Transfer Fee (1.5%)
                </span>
                <span className="font-medium" data-id="element-537">
                  {formatForeignCurrency(fee, currency)}
                </span>
              </div>
              <div className="pt-3 border-t border-gray-100 flex justify-between font-semibold text-brand-text-dark" data-id="element-538">
                <span data-id="element-539">Total to Pay</span>
                <span data-id="element-540">{formatForeignCurrency(totalCharge, currency)}</span>
              </div>
            </div>
          </div>
        </div>}

      {activeTab === 'beneficiaries' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-541">
          {beneficiaries.length > 0 ? beneficiaries.map((b, i) => <div key={i} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between" data-id="element-542">
                <div className="flex items-center gap-3" data-id="element-543">
                  <div className="w-10 h-10 bg-brand-primary/10 rounded-full flex items-center justify-center text-brand-primary font-bold" data-id="element-544">
                    {b.name.charAt(0)}
                  </div>
                  <div data-id="element-545">
                    <p className="font-semibold text-brand-text-dark text-sm" data-id="element-546">
                      {b.name}
                    </p>
                    <p className="text-xs text-brand-text-light" data-id="element-547">
                      {b.bank} • {b.account.slice(-4)}
                    </p>
                  </div>
                </div>
                <button onClick={() => fillBeneficiary(b)} className="p-2 text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors" title="Send Again" data-id="element-548">
                  <Send size={18} data-id="element-549" />
                </button>
              </div>) : <div className="col-span-full" data-id="element-550">
              <EmptyState icon={Users} title="No saved beneficiaries" description="Recipients will be saved here automatically after your first transfer." data-id="element-551" />
            </div>}
        </div>}

      {activeTab === 'history' && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-552">
          {state.transfers.length > 0 ? <div className="overflow-x-auto" data-id="element-553">
              <table className="w-full text-left text-sm" data-id="element-554">
                <thead className="bg-gray-50 text-brand-text-light border-b border-gray-100" data-id="element-555">
                  <tr data-id="element-556">
                    <th className="px-6 py-4 font-medium" data-id="element-557">Recipient</th>
                    <th className="px-6 py-4 font-medium" data-id="element-558">Date</th>
                    <th className="px-6 py-4 font-medium" data-id="element-559">Sent</th>
                    <th className="px-6 py-4 font-medium" data-id="element-560">Received</th>
                    <th className="px-6 py-4 font-medium" data-id="element-561">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-562">
                  {state.transfers.map(tx => <tr key={tx.id} className="hover:bg-gray-50/50 transition-colors" data-id="element-563">
                      <td className="px-6 py-4" data-id="element-564">
                        <p className="font-medium text-brand-text-dark" data-id="element-565">
                          {tx.recipientName}
                        </p>
                        <p className="text-xs text-brand-text-light" data-id="element-566">
                          {tx.bankName}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-brand-text-light" data-id="element-567">
                        {formatDate(tx.date)}
                      </td>
                      <td className="px-6 py-4 font-medium" data-id="element-568">
                        {formatForeignCurrency(tx.sourceAmount, tx.sourceCurrency)}
                      </td>
                      <td className="px-6 py-4 font-bold text-brand-text-dark" data-id="element-569">
                        {formatCurrency(tx.ngnAmount)}
                      </td>
                      <td className="px-6 py-4" data-id="element-570">
                        <StatusBadge status={tx.status} data-id="element-571" />
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div> : <div className="p-8" data-id="element-572">
              <EmptyState icon={History} title="No transfer history" description="Your international transfers will appear here." data-id="element-573" />
            </div>}
        </div>}

      {/* Confirmation Modal */}
      <Modal isOpen={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} title="Review Transfer" data-id="element-574">
        <div className="space-y-6" data-id="element-575">
          <div className="bg-gray-50 rounded-xl p-4 text-center" data-id="element-576">
            <p className="text-sm text-brand-text-light mb-1" data-id="element-577">
              You are sending
            </p>
            <p className="text-3xl font-bold text-brand-text-dark" data-id="element-578">
              {formatForeignCurrency(totalCharge, currency)}
            </p>
            <p className="text-xs text-brand-text-light mt-1" data-id="element-579">
              Includes {formatForeignCurrency(fee, currency)} fee
            </p>
          </div>

          <div className="space-y-3 text-sm" data-id="element-580">
            <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-581">
              <span className="text-brand-text-light" data-id="element-582">Recipient gets</span>
              <span className="font-bold text-brand-primary" data-id="element-583">
                {formatCurrency(ngnAmount)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-584">
              <span className="text-brand-text-light" data-id="element-585">Recipient Name</span>
              <span className="font-medium text-brand-text-dark" data-id="element-586">
                {recipientName}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-587">
              <span className="text-brand-text-light" data-id="element-588">Bank Details</span>
              <span className="font-medium text-brand-text-dark" data-id="element-589">
                {bankName} - {accountNumber}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-590">
              <span className="text-brand-text-light" data-id="element-591">Exchange Rate</span>
              <span className="font-medium text-brand-text-dark" data-id="element-592">
                1 {currency} = ₦{rate.toLocaleString()}
              </span>
            </div>
          </div>

          <button onClick={confirmTransfer} disabled={isProcessing} className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3.5 rounded-xl font-semibold transition-colors disabled:opacity-70 flex items-center justify-center" data-id="element-593">
            {isProcessing ? 'Processing...' : 'Confirm & Send'}
          </button>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal isOpen={isSuccessOpen} onClose={() => setIsSuccessOpen(false)} title="Transfer Successful" data-id="element-594">
        <div className="text-center py-6" data-id="element-595">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-green-500 mx-auto mb-4" data-id="element-596">
            <CheckCircle2 size={32} data-id="element-597" />
          </div>
          <h3 className="text-xl font-bold text-brand-text-dark mb-2" data-id="element-598">
            Money Sent!
          </h3>
          <p className="text-brand-text-light mb-6" data-id="element-599">
            Your transfer to {recipientName} has been initiated and will arrive
            instantly.
          </p>

          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left" data-id="element-600">
            <p className="text-xs text-brand-text-light mb-1" data-id="element-601">
              Reference Number
            </p>
            <p className="font-mono font-medium text-brand-text-dark" data-id="element-602">
              {currentRef}
            </p>
          </div>

          <button onClick={() => setIsSuccessOpen(false)} className="w-full bg-gray-100 hover:bg-gray-200 text-brand-text-dark py-3 rounded-xl font-semibold transition-colors" data-id="element-603">
            Done
          </button>
        </div>
      </Modal>
    </div>;
};